import React, { useContext, useEffect, useState } from "react";
import { FiltersContext } from "../FilterProvider/FilterProvider";
import FilterPanel from "../FilterPanel/FilterPanel";
import { DotLoader, TextButton } from "@SeemplicityDev/shared";
import "./SelectedFilterManagement.scss";
import Chip from "../../../Components/Chip/Chip";
import { capitalize } from "lodash-es";
import {
  filterEntityEnum,
  toLegacyEntityKey,
  type FilterEntity,
} from "../filterEntity";
import { FilterObject, FiltersToPin } from "../CommonFilterTypes";

export const getEntityText = (entity?: FilterEntity): string => {
  if (!entity) {
    return "";
  }

  switch (entity) {
    case filterEntityEnum.DATA_VALIDATION_FINDING:
    case filterEntityEnum.SEEMPLICITY_FINDING:
      return "Findings";
    default:
      return capitalize(toLegacyEntityKey(entity));
  }
};

interface SelectedFilterManagementProps {
  setDefaultsForScopeAndFilter?: boolean;
  showFilterActions?: boolean;
  hasEditMode?: boolean;
  readOnly?: boolean;
  enableSaveFilters?: boolean;
  hideSaveAs?: boolean;
  onFilterSave?: (filter: FilterObject) => void;
  entity?: FilterEntity;
  isLoadingFiltersData?: boolean;
  fieldsToIgnore?: string[];
  isInline?: boolean;
  fieldsToIgnoreWarningMsg?: string;
  filtersToPin?: FiltersToPin;
  fieldsToHide?: string[];
  unsupportedFilterFields?: string[];
  allowSaveWithPendingChanges?: boolean;
}

const SelectedFilterManagement: React.FC<SelectedFilterManagementProps> = ({
  setDefaultsForScopeAndFilter,
  showFilterActions,
  hasEditMode,
  readOnly,
  enableSaveFilters,
  hideSaveAs,
  onFilterSave,
  entity,
  isLoadingFiltersData,
  fieldsToIgnore = [],
  isInline = false,
  fieldsToIgnoreWarningMsg,
  filtersToPin,
  fieldsToHide = [],
  unsupportedFilterFields = [],
  allowSaveWithPendingChanges,
}) => {
  const {
    filtersState: { selectedFilter },
    updateFiltersState,
  } = useContext(FiltersContext);
  const [showChip, setShowChip] = useState(false);

  const setNewFilter = (): void => {
    setShowChip(false);
    updateFiltersState({
      selectedFilter: {
        label: "",
        value: {
          query: undefined,
          editable: true,
        },
      },
      filterToExecute: undefined,
    });
  };

  useEffect(() => {
    if (!selectedFilter?.value || selectedFilter?.value?.query === "{}") {
      setShowChip(true);
    } else {
      setShowChip(false);
    }
  }, [selectedFilter]);

  const allFilterChipEntity =
    entity === filterEntityEnum.REMEDIATION_ITEM ? undefined : entity;

  return (
    <div className={"selected-filter-management-wrap"}>
      {isLoadingFiltersData || !selectedFilter ? (
        <div className={"filter-dot-loader-wrap"}>
          <DotLoader className={"filter-dot-loader"} />
        </div>
      ) : showChip ? (
        <div className={"no-filter-conditions-wrap"}>
          <Chip
            leftIcon={"seem-icon-filter"}
            text={`All ${getEntityText(allFilterChipEntity)}`}
            disabled={true}
            rightIcon={undefined}
            small={false}
            square={false}
            noMargin={false}
            noHover={false}
            empty={false}
            tooltipContent={undefined}
          />
          <TextButton
            data-testid={"new-filter"}
            label={"New Filter"}
            leftIcon={"seem-icon-filter"}
            onClick={setNewFilter}
            buttonStyle={"textLink"}
          />
        </div>
      ) : (
        <FilterPanel
          showFilterActions={showFilterActions}
          hasEditMode={hasEditMode}
          setDefaultsForScopeAndFilter={setDefaultsForScopeAndFilter}
          readOnly={readOnly}
          onEmptyFilterPanel={() => setShowChip(true)}
          enableSaveFilters={enableSaveFilters}
          hideSaveAs={hideSaveAs}
          onFilterSave={onFilterSave}
          entity={entity}
          fieldsToIgnore={fieldsToIgnore}
          isInline={isInline}
          fieldsToIgnoreWarningMsg={fieldsToIgnoreWarningMsg}
          filtersToPin={filtersToPin}
          fieldsToHide={fieldsToHide}
          unsupportedFilterFields={unsupportedFilterFields}
          allowSaveWithPendingChanges={allowSaveWithPendingChanges}
        />
      )}
    </div>
  );
};

export default SelectedFilterManagement;
